import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import {
  Plus, Trash2, Eye, EyeOff, X, Save, RefreshCw, CheckCircle,
  ShieldCheck, ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/utils/errorHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAINS = [
  'voice', 'chatbots', 'hiring', 'kids', 'health',
  'fintech', 'image-video', 'agents', 'frontier',
];

const emptyArticle = {
  title: '',
  slug: '',
  hook: '',
  jurisdiction: '',
  domains: [],
  law_name: '',
  factbox: [],
  body_markdown: '',
  faq: [],
  sources: [],
};

const slugify = (t) =>
  (t || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-GB') : '—');

const SuperAdminLawWatch = () => {
  const { token } = useAuth();
  const authHdr = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null); // full article being edited or null
  const [creating, setCreating] = useState(false);
  const [savingBusy, setSavingBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [justPublished, setJustPublished] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/law-watch`, {
        headers: authHdr,
        params: statusFilter ? { status: statusFilter } : {},
      });
      setItems(res.data.items || []);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [authHdr, statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  const openCreate = () => {
    setEditing({ ...emptyArticle });
    setCreating(true);
    setJustPublished(false);
    setError('');
    setSuccess('');
  };

  const openEdit = async (id) => {
    setError(''); setSuccess('');
    try {
      const res = await axios.get(`${API}/admin/law-watch/${id}`, { headers: authHdr });
      setEditing(res.data);
      setCreating(false);
      setJustPublished(false);
    } catch (e) { setError(extractErrorMessage(e)); }
  };

  const closeEditor = () => {
    setEditing(null); setCreating(false); setJustPublished(false);
  };

  const setField = (k, v) => setEditing((cur) => ({ ...cur, [k]: v }));

  const toggleDomain = (d) => setEditing((cur) => ({
    ...cur,
    domains: cur.domains.includes(d) ? cur.domains.filter((x) => x !== d) : [...cur.domains, d],
  }));

  const addRow = (key, blank) => setEditing((cur) => ({ ...cur, [key]: [...(cur[key] || []), blank] }));
  const removeRow = (key, idx) => setEditing((cur) => ({ ...cur, [key]: (cur[key] || []).filter((_, i) => i !== idx) }));
  const patchRow = (key, idx, patch) => setEditing((cur) => ({
    ...cur, [key]: (cur[key] || []).map((r, i) => (i === idx ? { ...r, ...patch } : r)),
  }));

  const save = async () => {
    setSavingBusy(true); setError(''); setSuccess('');
    try {
      const payload = {
        title: editing.title,
        slug: creating ? (editing.slug || slugify(editing.title)) : undefined,
        hook: editing.hook,
        jurisdiction: editing.jurisdiction,
        domains: editing.domains,
        law_name: editing.law_name,
        factbox: editing.factbox,
        body_markdown: editing.body_markdown,
        faq: editing.faq,
        sources: editing.sources,
      };
      if (creating) {
        const res = await axios.post(`${API}/admin/law-watch`, payload, { headers: authHdr });
        setEditing(res.data);
        setCreating(false);
        setSuccess('Draft created.');
      } else {
        const res = await axios.put(`${API}/admin/law-watch/${editing.id}`, payload, { headers: authHdr });
        setEditing(res.data);
        setSuccess('Saved.');
      }
      await loadList();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSavingBusy(false); }
  };

  const publish = async () => {
    if (!editing?.id) return;
    setSavingBusy(true); setError(''); setSuccess('');
    try {
      const res = await axios.patch(`${API}/admin/law-watch/${editing.id}/publish`, {}, { headers: authHdr });
      setEditing(res.data);
      setJustPublished(true);
      setSuccess('Published.');
      await loadList();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSavingBusy(false); }
  };

  const unpublish = async (id) => {
    setSavingBusy(true); setError(''); setSuccess('');
    try {
      const res = await axios.patch(`${API}/admin/law-watch/${id}/unpublish`, {}, { headers: authHdr });
      if (editing?.id === id) setEditing(res.data);
      setSuccess('Unpublished. Article now returns 404 for public visitors.');
      await loadList();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSavingBusy(false); }
  };

  const verify = async (id) => {
    setSavingBusy(true); setError(''); setSuccess('');
    try {
      const res = await axios.patch(`${API}/admin/law-watch/${id}/verify`, {}, { headers: authHdr });
      if (editing?.id === id) setEditing(res.data);
      setSuccess('Verified timestamp updated. Body unchanged.');
      await loadList();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSavingBusy(false); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Soft-delete this article? (It will be moved to draft, not hard-deleted.)')) return;
    setSavingBusy(true); setError(''); setSuccess('');
    try {
      await axios.delete(`${API}/admin/law-watch/${id}`, { headers: authHdr });
      if (editing?.id === id) closeEditor();
      setSuccess('Article moved to draft.');
      await loadList();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSavingBusy(false); }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  if (editing) {
    const slugLocked = !creating && editing.status === 'published';
    return (
      <div className="min-h-screen bg-white p-6" data-testid="lw-editor">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-slate-900">
              {creating ? 'New Law Watch article' : 'Edit article'}
            </h1>
            <button onClick={closeEditor} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm" data-testid="lw-editor-close">
              <X className="w-4 h-4" /> Close
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="lw-editor-error">{error}</div>}
          {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded flex items-start gap-2" data-testid="lw-editor-success"><CheckCircle className="w-4 h-4 mt-0.5" /><span>{success}</span></div>}
          {justPublished && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded" data-testid="lw-editor-publish-note">
              <strong>Article is live now.</strong> Search-engine snapshot updates on next deploy.
            </div>
          )}

          {editing.id && (
            <div className="mb-4 text-xs text-slate-500 flex flex-wrap gap-x-6 gap-y-1">
              <span>Status: <strong className="text-slate-900">{editing.status}</strong></span>
              <span>Published: <strong className="text-slate-900">{fmtDate(editing.published_at)}</strong></span>
              <span>Verified: <strong className="text-slate-900">{fmtDate(editing.verified_at)}</strong></span>
              <span>Updated: <strong className="text-slate-900">{fmtDate(editing.updated_at)}</strong></span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={editing.title || ''}
                onChange={(e) => setField('title', e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                data-testid="lw-field-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Slug {slugLocked && <span className="text-xs text-amber-700">(locked after publish)</span>}
              </label>
              <input
                type="text"
                value={editing.slug || ''}
                onChange={(e) => !slugLocked && setField('slug', e.target.value)}
                onBlur={(e) => !slugLocked && setField('slug', slugify(e.target.value))}
                placeholder={slugify(editing.title || '')}
                disabled={slugLocked}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono disabled:bg-slate-100"
                data-testid="lw-field-slug"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Hook (index teaser, 1–2 sentences)</label>
            <textarea
              rows={2}
              value={editing.hook || ''}
              onChange={(e) => setField('hook', e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="lw-field-hook"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Jurisdiction</label>
              <input
                type="text"
                value={editing.jurisdiction || ''}
                onChange={(e) => setField('jurisdiction', e.target.value)}
                placeholder="Connecticut · Federal · NYC"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                data-testid="lw-field-jurisdiction"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Law name</label>
              <input
                type="text"
                value={editing.law_name || ''}
                onChange={(e) => setField('law_name', e.target.value)}
                placeholder="Connecticut SB 5 / Public Act 26-15"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                data-testid="lw-field-law-name"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Domains (pick all that apply)</label>
            <div className="flex flex-wrap gap-2" data-testid="lw-field-domains">
              {DOMAINS.map((d) => {
                const on = editing.domains.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDomain(d)}
                    className={`px-3 py-1 text-xs rounded-full border ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-300'}`}
                    data-testid={`lw-domain-${d}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Factbox */}
          <div className="mb-4 border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Fact-box rows (label / value)</label>
              <button onClick={() => addRow('factbox', { label: '', value: '' })} className="text-xs text-emerald-700 flex items-center gap-1" data-testid="lw-factbox-add">
                <Plus className="w-3 h-3" /> Row
              </button>
            </div>
            {(editing.factbox || []).map((r, i) => (
              <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2 mb-2" data-testid={`lw-factbox-row-${i}`}>
                <input value={r.label} onChange={(e) => patchRow('factbox', i, { label: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Law" />
                <input value={r.value} onChange={(e) => patchRow('factbox', i, { value: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Value" />
                <button onClick={() => removeRow('factbox', i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Body markdown */}
          <div className="mb-4" data-color-mode="light">
            <label className="block text-sm font-medium text-slate-700 mb-1">Body (Markdown — H2/H3, lists, links, tables, blockquote. Prefix a blockquote with <code>CASE:</code> to render the amber &quot;case to know&quot; block.)</label>
            <MDEditor
              value={editing.body_markdown || ''}
              onChange={(v) => setField('body_markdown', v || '')}
              height={480}
              preview="live"
              data-testid="lw-field-body"
            />
          </div>

          {/* FAQ */}
          <div className="mb-4 border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">FAQ (0–8 items — plain Q&amp;A on the article)</label>
              <button onClick={() => addRow('faq', { question: '', answer: '' })} className="text-xs text-emerald-700 flex items-center gap-1" data-testid="lw-faq-add">
                <Plus className="w-3 h-3" /> Q&amp;A
              </button>
            </div>
            {(editing.faq || []).map((r, i) => (
              <div key={i} className="mb-3" data-testid={`lw-faq-row-${i}`}>
                <input value={r.question} onChange={(e) => patchRow('faq', i, { question: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-sm mb-1" placeholder="Question" />
                <div className="flex gap-2">
                  <textarea rows={2} value={r.answer} onChange={(e) => patchRow('faq', i, { answer: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Answer" />
                  <button onClick={() => removeRow('faq', i)} className="text-slate-400 hover:text-red-600 self-start pt-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Sources */}
          <div className="mb-6 border border-slate-200 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-700">Source trail</label>
              <button onClick={() => addRow('sources', { title: '', url: 'https://' })} className="text-xs text-emerald-700 flex items-center gap-1" data-testid="lw-sources-add">
                <Plus className="w-3 h-3" /> Source
              </button>
            </div>
            {(editing.sources || []).map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2" data-testid={`lw-source-row-${i}`}>
                <input value={r.title} onChange={(e) => patchRow('sources', i, { title: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Source title" />
                <input value={r.url} onChange={(e) => patchRow('sources', i, { url: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm font-mono" placeholder="https://" />
                <button onClick={() => removeRow('sources', i)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white py-4">
            <button
              onClick={save}
              disabled={savingBusy}
              className="bg-slate-900 text-white text-sm px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50"
              data-testid="lw-editor-save"
            >
              <Save className="w-4 h-4" /> {creating ? 'Create draft' : 'Save changes'}
            </button>
            {editing.id && editing.status !== 'published' && (
              <button
                onClick={publish}
                disabled={savingBusy}
                className="bg-emerald-600 text-white text-sm px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50"
                data-testid="lw-editor-publish"
              >
                <Eye className="w-4 h-4" /> Publish
              </button>
            )}
            {editing.id && editing.status === 'published' && (
              <>
                <button
                  onClick={() => unpublish(editing.id)}
                  disabled={savingBusy}
                  className="bg-white text-slate-700 border border-slate-300 text-sm px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50"
                  data-testid="lw-editor-unpublish"
                >
                  <EyeOff className="w-4 h-4" /> Unpublish
                </button>
                <button
                  onClick={() => verify(editing.id)}
                  disabled={savingBusy}
                  className="bg-white text-slate-700 border border-slate-300 text-sm px-4 py-2 rounded flex items-center gap-2 disabled:opacity-50"
                  data-testid="lw-editor-verify"
                >
                  <ShieldCheck className="w-4 h-4" /> Verify (stamp)
                </button>
                <a
                  href={`/ai-law-watch/${editing.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 text-sm inline-flex items-center gap-1 ml-auto"
                  data-testid="lw-editor-view"
                >
                  <ExternalLink className="w-4 h-4" /> View live
                </a>
              </>
            )}
            {editing.id && (
              <button
                onClick={() => doDelete(editing.id)}
                disabled={savingBusy}
                className="text-red-600 text-sm px-2 py-2 flex items-center gap-1 ml-auto"
                data-testid="lw-editor-delete"
              >
                <Trash2 className="w-4 h-4" /> Soft-delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="lw-list">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">AI Law Watch — articles</h1>
          <button
            onClick={openCreate}
            className="bg-emerald-600 text-white text-sm px-4 py-2 rounded flex items-center gap-2"
            data-testid="lw-list-new"
          >
            <Plus className="w-4 h-4" /> New article
          </button>
        </div>

        <div className="mb-4 flex items-center gap-2 text-sm">
          <span className="text-slate-600">Filter:</span>
          {['', 'draft', 'published'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded border ${statusFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`}
              data-testid={`lw-filter-${s || 'all'}`}
            >
              {s || 'all'}
            </button>
          ))}
          <button
            onClick={loadList}
            className="ml-auto text-slate-500 hover:text-slate-900 flex items-center gap-1"
            data-testid="lw-list-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
        {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5" /><span>{success}</span></div>}

        <div className="bg-white border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2.5 text-left">Title</th>
                <th className="px-4 py-2.5 text-left">Jurisdiction</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Published</th>
                <th className="px-4 py-2.5 text-left">Verified</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-6 text-slate-500 text-center">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-slate-500 text-center" data-testid="lw-list-empty">No articles yet. Click &quot;New article&quot; to start.</td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`lw-row-${a.slug}`}>
                  <td className="px-4 py-3 text-slate-900 font-medium">{a.title}</td>
                  <td className="px-4 py-3 text-slate-700">{a.jurisdiction}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${a.status === 'published' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(a.published_at)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(a.verified_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(a.id)} className="text-slate-700 hover:text-slate-900 text-xs mr-3" data-testid={`lw-edit-${a.slug}`}>Edit</button>
                    {a.status === 'published' ? (
                      <button onClick={() => unpublish(a.id)} className="text-slate-500 hover:text-red-600 text-xs" data-testid={`lw-unpublish-${a.slug}`}>Unpublish</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLawWatch;
